import clsx from "clsx";
import DefaultImage from "assets/Images/BrandImage1.png";
import { ClaimIcon, MerchDropIcon, PollIcon, AirdropMementoIcon } from "components/atoms/Icon";
import { CheckCircleIcon } from "@heroicons/react/20/solid";

type Props = {
  name: string;
  imageUrl: string;
  additionalClasses?: string;
  mementoType: string;
  actionPending?: boolean;
  owns?: boolean;
};

interface ClickableCard extends Props {
  isClickable: boolean;
  handleOnClick: () => void;
  activationIconURL?: string;
  textDark?: boolean;
}
interface ViewOnlyCard extends Props {
  isClickable?: never;
  handleOnClick?: never;
  activationIconURL?: never;
  textDark?: boolean;
}

const getMementoIcon = (mementoName: string, actionPending?: boolean) => {
  switch (mementoName) {
    case "mem_drop":
      return (
        <AirdropMementoIcon className="h-3 w-3 md:h-5 md:w-5" color={actionPending ? "text-[#000080]" : undefined} />
      );
    case "merch_drop":
      return <MerchDropIcon className="h-3 w-3 md:h-5 md:w-5" color={actionPending ? "text-[#000080]" : undefined} />;
    case "claim_code":
      return <ClaimIcon className="h-3 w-3 md:h-5 md:w-5" color={actionPending ? "text-[#000080]" : undefined} />;
    case "poll":
      return <PollIcon className="h-3 w-3 md:h-5 md:w-5" color={actionPending ? "text-[#000080]" : undefined} />;
    default:
      return (
        <AirdropMementoIcon className="h-3 w-3 md:h-5 md:w-5" color={actionPending ? "text-[#000080]" : undefined} />
      );
  }
};

function MagnetPageMementoCard({
  name,
  imageUrl,
  additionalClasses = "",
  isClickable = false,
  handleOnClick,
  mementoType,
  actionPending,
  activationIconURL,
  owns,
}: ClickableCard | ViewOnlyCard) {
  return (
    <div className={clsx("flex w-full flex-col", isClickable && "cursor-pointer")} onClick={handleOnClick}>
      <div className="relative">
        <div className="aspect-h-1 aspect-w-1">
          <img
            src={imageUrl || DefaultImage}
            alt={name}
            className={clsx(
              "rounded border-[0.1px] object-cover md:rounded-ll md:border",
              actionPending ? "border-accent" : "border-white",
            )}
          />
        </div>
        <div
          className={clsx(
            "absolute bottom-0 right-0 rounded-br rounded-tl  p-1 md:rounded-br-ll  md:rounded-tl-ll",
            actionPending ? "bg-yellow-400" : "bg-[#8B8B8B]",
          )}
        >
          {activationIconURL ? <img src={activationIconURL} alt="icon" /> : getMementoIcon(mementoType, actionPending)}
        </div>
        {owns && !actionPending && (
          <div
            className={clsx(
              "absolute right-0 top-0 rounded-br rounded-tl  p-1 md:rounded-br-ll  md:rounded-tl-ll",
              actionPending ? "bg-yellow-400" : "bg-[#8B8B8B]",
            )}
          >
            <CheckCircleIcon className="h-5 w-5 text-lime-600" />
          </div>
        )}
      </div>
      <p
        className={clsx(
          "mt-2 truncate text-left font-satoshi",
          "text-base font-bold  leading-5 text-primary first-letter:uppercase sm:text-base md:text-xxs 2xl:mt-1",
          additionalClasses,
        )}
      >
        {name}
      </p>
    </div>
  );
}

export function MagnetPageMementoCardDesktop({
  name,
  imageUrl,
  additionalClasses = "",
  isClickable = false,
  handleOnClick,
  mementoType,
  actionPending,
  owns,
  activationIconURL,
  textDark = false,
}: ClickableCard | ViewOnlyCard) {
  return (
    <div
      className={clsx("flex w-14 flex-col xl:w-32 2xl:w-48", isClickable && "cursor-pointer")}
      tabIndex={0}
      onKeyDown={() => {}}
      role="button"
      onClick={(e) => {
        e.stopPropagation();
        if (handleOnClick) handleOnClick();
      }}
    >
      <div className="relative">
        <div className=" aspect-1 h-14 w-14 xl:h-32 xl:w-32 2xl:h-48 2xl:w-48">
          <img
            src={imageUrl || DefaultImage}
            alt={name}
            className={clsx(
              "h-full w-full rounded border-[0.1px] object-cover   md:rounded-ll md:border ",
              actionPending ? "border-accent" : "border-white border-opacity-30",
            )}
          />
        </div>
        <div
          className={clsx(
            "absolute bottom-0 right-0 rounded-br rounded-tl  p-1 md:rounded-br-ll  md:rounded-tl-ll",
            actionPending ? "bg-yellow-400" : "bg-[#8B8B8B] opacity-80",
          )}
        >
          {activationIconURL ? <img src={activationIconURL} alt="icon" /> : getMementoIcon(mementoType, actionPending)}
        </div>
        {owns && !actionPending && (
          <div
            title="Completed and Memento Acquired"
            className={clsx("absolute right-0 top-0 rounded-bl-ll rounded-tr-ll bg-black bg-opacity-75 p-1")}
          >
            <CheckCircleIcon className="h-5 w-5 text-lime-600" />
          </div>
        )}
      </div>
      <p
        className={clsx(
          "mt-0.5 truncate text-left font-satoshi",
          "text-xxs font-bold  leading-5 first-letter:uppercase sm:text-base md:text-xxs 2xl:mt-2",
          textDark ? "text-primary" : "text-white",
          additionalClasses,
        )}
      >
        {name}
      </p>
    </div>
  );
}

export default MagnetPageMementoCard;
